import { EventType } from '../experiments/Events.ts';
import type { EventLogger } from '../experiments/EventLogger.ts';
import type { GameState } from '../game/GameState.ts';
import type { EmgSample, InputDevice } from '../hardware/InputDevice.ts';
import { EdgeLatch, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

export interface EmgParams {
  /**
   * Resting level, device units. Subtracted before rectification. Set by
   * `calibrateBaseline()`; the default is a placeholder, not a real value.
   */
  baseline: number;
  /** Envelope smoothing time constant, ms. ~50 ms is the usual starting point. */
  envelopeTauMs: number;
  /** Envelope level that starts a burst, device units above baseline. */
  onThreshold: number;
  /**
   * Envelope level that ends it. Must be below `onThreshold`; the gap is what
   * stops a signal hovering at threshold from producing a burst per sample.
   */
  offThreshold: number;
  /** Minimum time between two accepted bursts, ms. */
  refractoryMs: number;
  /** Channel to watch. */
  channel: number;
}

export const DEFAULT_EMG_PARAMS: EmgParams = {
  baseline: 40,
  envelopeTauMs: 40,
  onThreshold: 90,
  offThreshold: 45,
  refractoryMs: 200,
  channel: 0,
};

/**
 * EMG threshold control: a muscle contraction flaps the bird.
 *
 * Signal path, all of it deliberately simple and inspectable:
 *
 *   sample -> subtract baseline -> rectify -> one-pole low-pass (envelope)
 *          -> Schmitt trigger (onThreshold / offThreshold) -> refractory
 *          -> EMG_THRESHOLD_CROSSED -> latch -> flap
 *
 * The Schmitt trigger and the refractory period are not optional niceties. A
 * bare threshold on a rectified EMG envelope produces a burst of a dozen
 * "movements" per contraction, which would make the successful-command rate in
 * the results meaningless.
 *
 * The envelope time constant is real latency: a 40 ms filter costs roughly
 * 40 ms of detection delay on top of the participant's own response time. That
 * shows up in the calibration measurements, which is exactly where it belongs.
 */
export class EmgController implements Controller {
  readonly id = 'emg';
  readonly label: string;

  params: EmgParams;

  private latch = new EdgeLatch();
  private unsubscribe: (() => void) | null = null;
  private envelope = 0;
  private lastSampleAt: number | null = null;
  private above = false;
  private lastBurstAt = -Infinity;
  private burstCount = 0;
  private sampleCount = 0;
  /** Recent envelope values for the dashboard trace. */
  private trace: number[] = [];

  constructor(
    private readonly device: InputDevice,
    private readonly logger: EventLogger,
    private readonly getTick: () => number,
    params: Partial<EmgParams> = {},
  ) {
    this.params = { ...DEFAULT_EMG_PARAMS, ...params };
    this.label = `EMG threshold (${device.label})`;
  }

  async attach(): Promise<void> {
    if (this.unsubscribe) return;
    if (this.device.getStatus() !== 'connected') await this.device.connect();
    this.unsubscribe = this.device.onEmg((s) => this.onSample(s));
  }

  async detach(): Promise<void> {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.latch.clear();
  }

  reset(): void {
    this.latch.clear();
    this.envelope = 0;
    this.above = false;
    this.lastSampleAt = null;
    this.lastBurstAt = -Infinity;
  }

  update(_state: GameState): ControllerOutput {
    const edge = this.latch.consume();
    if (!edge) return noFlap();
    return { flap: true, timestamp: edge.timestamp, metadata: edge.meta };
  }

  status(): Record<string, unknown> {
    return {
      envelope: Math.round(this.envelope),
      onThreshold: this.params.onThreshold,
      offThreshold: this.params.offThreshold,
      above: this.above,
      bursts: this.burstCount,
      samples: this.sampleCount,
      connection: this.device.getStatus(),
    };
  }

  /** Envelope history for the dashboard sparkline, oldest first. */
  getTrace(): readonly number[] {
    return this.trace;
  }

  /**
   * Measure the resting level from the last `n` samples of quiet.
   * Timing calibration, not intensity calibration -- this touches nothing on
   * any stimulator.
   */
  calibrateBaseline(restingSamples: readonly number[]): number {
    if (restingSamples.length === 0) return this.params.baseline;
    const mean = restingSamples.reduce((a, b) => a + b, 0) / restingSamples.length;
    this.params.baseline = mean;
    return mean;
  }

  private onSample(s: EmgSample): void {
    if (s.channel !== this.params.channel) return;
    this.sampleCount++;

    const dt = this.lastSampleAt === null ? 0 : s.timestamp - this.lastSampleAt;
    this.lastSampleAt = s.timestamp;

    const rectified = Math.abs(s.value - this.params.baseline);
    // One-pole low-pass, dt-aware so it behaves the same at any sample rate.
    const alpha = dt > 0 ? 1 - Math.exp(-dt / Math.max(1, this.params.envelopeTauMs)) : 1;
    this.envelope += alpha * (rectified - this.envelope);

    this.trace.push(this.envelope);
    if (this.trace.length > 300) this.trace.shift();

    if (!this.above && this.envelope >= this.params.onThreshold) {
      this.above = true;
      if (s.timestamp - this.lastBurstAt >= this.params.refractoryMs) {
        this.lastBurstAt = s.timestamp;
        this.burstCount++;
        this.logger.log(EventType.EMG_THRESHOLD_CROSSED, {
          tick: this.getTick(),
          timestamp: s.timestamp,
          metadata: {
            envelope: this.envelope,
            onThreshold: this.params.onThreshold,
            channel: s.channel,
          },
        });
        // Logged as BUTTON_DOWN too, so the latency tracker treats an EMG burst
        // and a switch closure as the same pipeline stage.
        this.logger.log(EventType.BUTTON_DOWN, {
          tick: this.getTick(),
          timestamp: s.timestamp,
          metadata: { source: 'emg', envelope: this.envelope },
        });
        this.latch.set(s.timestamp, { source: 'emg', envelope: this.envelope });
      }
    } else if (this.above && this.envelope <= this.params.offThreshold) {
      this.above = false;
      this.logger.log(EventType.EMG_BELOW_THRESHOLD, {
        tick: this.getTick(),
        timestamp: s.timestamp,
        metadata: { envelope: this.envelope },
      });
    }
  }
}
